import React from 'react';
import { Check } from 'lucide-react';
import { fileUrl } from '@/lib/api';
import { useMotion, useReveal, useMagnetic, useCursorSpotlight } from '@/lib/motion';

const DEFAULT_SWOOGO = 'https://circle.swoogo.com/SEI_ConIII';

export default function RegistrationSection({ registration, section = {}, meta = {}, motion = {} }) {
  const fa = registration.full_access || {};
  const allInd = registration.individual_experiences || [];
  // Only render visible individual experiences (admin can hide). Sort by order.
  const indExps = [...allInd].filter((e) => e.visible !== false).sort((a, b) => (a.order || 0) - (b.order || 0));
  const hotel = registration.hotel || {};
  const headerReveal = useReveal();
  const colReveal = useReveal();
  const { motionAllowed } = useMotion(); // eslint-disable-line no-unused-vars
  const magneticEnabled = motion?.wow?.magnetic_cursor !== false;
  const spotlightEnabled = motion?.wow?.cursor_spotlight_registration !== false;
  // Registration now renders on a solid bg-card-dark background; typography stays
  // light-on-dark always (admin Motion & Visual section settings no longer affect
  // this section's background — flagged but intentionally untouched).
  const isImageBg = true;
  const titleColor = 'text-white';
  const subTitleColor = 'text-white/70';

  const fullAccessBtn = useMagnetic(magneticEnabled);
  const hotelBtn = useMagnetic(magneticEnabled);
  const spotlightRef = useCursorSpotlight(spotlightEnabled);

  const eyebrow = meta.eyebrow || 'ONE REGISTRATION PLATFORM.';
  const headline = meta.headline || 'MULTIPLE WAYS TO EXPERIENCE SEICON III.';

  const faName = fa.name || 'Full Access Pass';
  const bestValueLabel = fa.best_value_label || 'Best Value';
  const selectLabel = fa.select_label || 'Select Full Access Pass';
  const swoogoUrl = fa.swoogo_url || DEFAULT_SWOOGO;

  const upgradeEyebrow = registration.upgrade_eyebrow || 'Individual Experiences';
  const upgradeBoxTitle = registration.upgrade_box_title || 'Add-On Experiences & Upgrades';
  const upgradeBoxBody = registration.upgrade_box_body || 'Upgrade any pass to Full Access and unlock it all.';
  const upgradeButtonLabel = registration.upgrade_box_button_label || 'View Upgrade Options';
  const indLearnMore = registration.individual_learn_more_label || 'Learn More';
  const indComingSoon = registration.individual_coming_soon_label || 'Coming Soon';

  const hotelEyebrow = registration.hotel_eyebrow || 'Official Hotel';
  const hotelSelectLabel = registration.hotel_select_label || 'Book Hotel';

  return (
    <section
      id="registration"
      className={`section-pad relative bg-card-dark ${spotlightEnabled ? 'cursor-spotlight' : ''}`}
      ref={spotlightRef}
      data-testid="section-registration"
    >
      <div className="container-x relative z-10">
          <div ref={headerReveal.ref} className={`${headerReveal.simple ? 'reveal-simple' : 'reveal-up'} ${headerReveal.revealed ? 'is-in' : ''}`}>
            {/* v3.15 — Eyebrow size from site_settings.registration.eyebrow_size
                (NOT sections_meta — per storage-key contract, this section's
                size lives alongside the other registration.* fields).
                Defensive validation: integer in [16, 48]. */}
            {(() => {
              const _raw = registration.eyebrow_size;
              const eyebrowSize = typeof _raw === 'number' && Number.isInteger(_raw) && _raw >= 16 && _raw <= 48 ? _raw : 26;
              return (
                <p className="eyebrow eyebrow-lg" data-testid="registration-eyebrow" style={{ '--eyebrow-size': `${eyebrowSize}px` }}>{eyebrow}</p>
              );
            })()}
            <h2 className={`mt-4 font-display font-bold uppercase ${titleColor} leading-[1.05] tracking-tightish text-[36px] md:text-[40px] lg:text-[56px]`} data-testid="registration-headline">
              {headline}
            </h2>
          </div>

          <div ref={colReveal.ref} className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-6">
            <div
              className={`lg:col-span-4 bg-bg-elevated border border-border-line rounded-lg p-6 shadow-[0_4px_24px_rgba(14,14,16,0.10)] ${colReveal.simple ? 'reveal-simple' : 'reveal-card'} ${colReveal.revealed ? 'is-in' : ''}`}
              style={{ transitionDelay: '0ms' }}
              data-testid="full-access-card"
            >
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <h3 className="font-display font-bold uppercase text-ink-primary text-[20px] tracking-tightish" data-testid="full-access-name">{faName}</h3>
                <span className="font-body font-bold text-[10px] uppercase tracking-wider2 bg-brand-orange text-white px-[10px] py-[4px] rounded">{bestValueLabel}</span>
              </div>
              <div className="mt-3 flex items-baseline gap-3 flex-wrap">
                <span className="font-display font-bold text-brand-orange text-[44px] md:text-[48px] leading-none tracking-tightish">{fa.price || '$699'}</span>
                <span className="font-body font-medium text-[11px] uppercase text-ink-muted tracking-wider2">{fa.deadline || 'EARLY BIRD ENDS JULY 5'}</span>
              </div>
              <p className="mt-3 font-body text-[14px] text-ink-muted">{fa.description || 'Everything included. The complete SEICON III experience.'}</p>
              <ul className="mt-5 space-y-2.5">
                {(fa.bullets || []).map((b, i) => (
                  <li key={i} className="flex items-start gap-2 font-body text-[14px] text-ink-primary">
                    <Check size={16} className="text-brand-orange mt-0.5 shrink-0" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
              <span ref={fullAccessBtn} className="block mt-6">
                <a href={swoogoUrl} target="_blank" rel="noopener noreferrer" className="btn-primary w-full" data-testid="select-full-access">{selectLabel}</a>
              </span>
            </div>

            <div className={`lg:col-span-5 ${colReveal.simple ? 'reveal-simple' : 'reveal-card'} ${colReveal.revealed ? 'is-in' : ''}`} style={{ transitionDelay: '80ms' }} data-testid="individual-experiences">
              <p className="font-display font-semibold uppercase text-brand-orange text-[12px] tracking-wider4" data-testid="upgrade-eyebrow">{upgradeEyebrow}</p>
              <div className="mt-4 grid grid-cols-2 md:grid-cols-3 gap-4">
                {indExps.map((e, i) => {
                  const isComingSoon = !!e.coming_soon;
                  return (
                    <a
                      key={e.id || i}
                      href={isComingSoon ? '#' : (e.url || '#')}
                      onClick={isComingSoon ? (ev) => ev.preventDefault() : undefined}
                      aria-disabled={isComingSoon}
                      tabIndex={isComingSoon ? -1 : 0}
                      className={`group relative block overflow-hidden rounded-md bg-card-dark transition-transform duration-300 ${isComingSoon ? 'cursor-default' : 'hover:-translate-y-1'}`}
                      style={{ aspectRatio: '1/1' }}
                      data-testid="individual-exp-card"
                    >
                      <img src={fileUrl(e.image)} alt={e.title} className="absolute inset-0 w-full h-full object-cover opacity-90 group-hover:opacity-100 transition" />
                      <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, rgba(0,0,0,0) 35%, rgba(0,0,0,0.7) 100%)' }} />
                      <div className="absolute inset-x-0 bottom-0 p-3">
                        <h4 className="font-display font-bold uppercase text-white text-[13px] leading-tight tracking-tightish">{e.title}</h4>
                        <div className="mt-1 flex items-baseline justify-between gap-2">
                          <span className="font-display font-bold text-brand-orange text-[16px] leading-none">{e.price}</span>
                          {isComingSoon ? (
                            <span className="font-body font-bold text-[9px] uppercase tracking-wider2 bg-brand-orange text-white px-2 py-0.5 rounded" data-testid="ind-coming-soon">{indComingSoon}</span>
                          ) : (
                            <span className="font-body font-semibold text-[10px] uppercase text-brand-orange tracking-wider2">{indLearnMore} <span className="arrow-nudge">→</span></span>
                          )}
                        </div>
                      </div>
                    </a>
                  );
                })}
              </div>
              <div className={`mt-6 p-5 ${isImageBg ? 'border-white/20 bg-card-dark/30 backdrop-blur-sm' : 'border-border-line'} border rounded-md`}>
                <p className={`font-display font-semibold uppercase ${isImageBg ? 'text-white' : 'text-ink-primary'} text-[14px] tracking-tightish`} data-testid="upgrade-box-title">{upgradeBoxTitle}</p>
                <p className={`mt-1 font-body font-medium text-[13px] ${subTitleColor}`} data-testid="upgrade-box-body">{upgradeBoxBody}</p>
                <a href={swoogoUrl} target="_blank" rel="noopener noreferrer" className={`btn-secondary mt-4 !block w-fit mx-auto text-center ${isImageBg ? '!border-white !text-white hover:!bg-white hover:!text-card-dark' : ''}`} data-testid="view-upgrade-options">{upgradeButtonLabel}</a>
              </div>
            </div>

            <div id="hotel" className={`lg:col-span-3 bg-bg-elevated border border-border-line rounded-lg p-6 ${colReveal.simple ? 'reveal-simple' : 'reveal-card'} ${colReveal.revealed ? 'is-in' : ''}`} style={{ transitionDelay: '160ms' }} data-testid="hotel-card">
              <p className="font-display font-semibold uppercase text-brand-orange text-[11px] tracking-wider4" data-testid="hotel-eyebrow">{hotelEyebrow}</p>
              <h3 className="mt-1 font-display font-bold uppercase text-ink-primary text-[22px] tracking-tightish leading-tight">{hotel.name || 'BELLAGIO HOTEL & CASINO'}</h3>
              <div className="mt-4 overflow-hidden rounded-sm" style={{ aspectRatio: '16/9' }}>
                <img src={fileUrl(hotel.image)} alt="Bellagio Hotel" className="w-full h-full object-cover" />
              </div>
              <ul className="mt-4 space-y-2">
                {(hotel.perks || []).map((p, i) => (
                  <li key={i} className="flex items-start gap-2 font-body text-[13px] text-ink-primary">
                    <Check size={15} className="text-brand-orange mt-0.5 shrink-0" />
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-4 font-body font-semibold text-[11px] uppercase text-brand-orange tracking-wider2">{hotel.deadline || 'BOOK BY JULY 5, 2026'}</p>
              <span ref={hotelBtn} className="block mt-3">
                <a href={hotel.booking_url || '#'} target={hotel.booking_url && hotel.booking_url !== '#' ? '_blank' : undefined} rel="noopener noreferrer" className="btn-primary w-full" data-testid="book-hotel">{hotelSelectLabel}</a>
              </span>
            </div>
          </div>
        </div>
    </section>
  );
}
